import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { getFiveMinuteIntervalMinute } from "_utils/functions";
import * as TimelineFlightService from "_services/timeline-flight-service";
import * as FlightService from "_services/flight-service";

/**
 * Get timeline flights
 * Returns flight information data (array or time-based dictionary)
 */
export const getFlights = createAsyncThunk(
    "timelineFlightList/getFlights",
    async ({ iata, startDate, endDate, type, groupByTime, thirtyMinLayout, boardingFlights, rampTasks, selectedAirportArea, showAllFlights, jobsType = "all", holdingArea = false, holdingLocation = null, rampForAllLocs = false, rampForHCAndHS = false, includeFlights = [] }) => {
        return TimelineFlightService.getTimelineFlights({
            iata,
            startDate,
            endDate,
            type,
            groupByTime,
            thirtyMinLayout,
            boardingFlights,
            rampTasks,
            selectedAirportArea,
            showAllFlights,
            jobsType,
            holdingArea,
            holdingLocation,
            rampForAllLocs,
            rampForHCAndHS,
            includeFlights
        });
    }
);

/**
 * Update a flight document (e.g., notes)
 * @param {Object} params - { id: flightId, body: { ...fieldsToUpdate } }
 */
export const updateFlight = createAsyncThunk(
    "timelineFlightList/updateFlight",
    async (params) => {
        return FlightService.patchFlight(params);
    }
);

export const getFlightById = createAsyncThunk(
    "timelineFlightList/getFlightById",
    async (flightId) => {
        return FlightService.getFlightById(flightId);
    }
);

export const getThirdStateAirports = createAsyncThunk(
    "timelineFlightList/getThirdStateAirports",
    async (iata) => {
        return FlightService.getThirdStateAirports(iata);
    }
)

const timelineFlightListSlice = createSlice({
    name: 'timelineFlightList',
    initialState: {
        flightsByTime: {}, // Time-based dictionary: { "08:00": [flight1, flight2], "09:00": [...] }
        status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
        error: null,
        queryTime: null, // The last time we fetched data
        updateFlightStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
        updateFlightError: null,
        flightSearchTerm: '', // Search term for filtering flights
        flightFromDBById: null,
        getFlightByIdStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
        getFlightByIdError: null,

        // third state airports
        thirdStateAirportsError: null,
        thirdStateAirportsStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
        thirdStateAirports: null, // array of iata
    },
    reducers: {
        reset: (state) => {
            state.flightsByTime = {};
            state.status = 'idle';
            state.error = null;
            state.queryTime = null;
            state.flightSearchTerm = '';
        },
        setQueryTime: (state, action) => {
            state.queryTime = action.payload;
        },
        resetUpdateFlightStatus: (state) => {
            state.updateFlightStatus = 'idle';
            state.updateFlightError = null;
        },
        setFlightSearchTerm: (state, action) => {
            state.flightSearchTerm = action.payload;
        },
        resetGetFlightByIdStatus: (state) => {
            state.getFlightByIdStatus = 'idle';
            state.getFlightByIdError = null;
        },
        resetGetFlightFromDBById: (state) => {
            state.flightFromDBById = null;
            state.getFlightByIdStatus = 'idle';
            state.getFlightByIdError = null;
        },
    },
    extraReducers: {
        [getFlights.pending]: (state, action) => {
            state.status = "loading";
            state.error = null;
        },
        [getFlights.fulfilled]: (state, action) => {
            state.status = "succeeded";
            state.flightsByTime = action.payload || {};
            state.queryTime = new Date(); // Cache query time
        },
        [getFlights.rejected]: (state, action) => {
            state.status = "failed";
            state.flightsByTime = {};
            state.error = action.error.message;
        },
        // Update flight
        [updateFlight.pending]: (state) => {
            state.updateFlightStatus = "loading";
            state.updateFlightError = null;
        },
        [updateFlight.fulfilled]: (state, action) => {
            state.updateFlightStatus = "succeeded";
            // Update the flight in state if it exists
            const updatedFlight = action.payload;
            console.log(' state.flightsByTime', state.flightsByTime)

            console.log('updatedFlight', updatedFlight);
            if (updatedFlight?._id) {
                for (const time in state.flightsByTime) {
                    const index = state.flightsByTime[time].findIndex(f => f._id === updatedFlight._id || f.bookingKey === updatedFlight._id);
                    if (index !== -1) {
                        state.flightsByTime[time][index] = { ...state.flightsByTime[time][index], ...updatedFlight };
                        break;
                    }
                }
            }
        },
        [updateFlight.rejected]: (state, action) => {
            state.updateFlightStatus = "failed";
            state.updateFlightError = action.error.message;
        },
        // Get flight by id
        [getFlightById.pending]: (state) => {
            state.getFlightByIdStatus = "loading";
            state.getFlightByIdError = null;
        },
        [getFlightById.fulfilled]: (state, action) => {
            state.getFlightByIdStatus = "succeeded";
            state.flightFromDBById = action.payload || null;
        },
        [getFlightById.rejected]: (state, action) => {
            state.getFlightByIdStatus = "failed";
            state.flightFromDBById = null;
            state.getFlightByIdError = action.error.message;
        },

        // Get third state airports
        [getThirdStateAirports.pending]: (state) => {
            state.thirdStateAirportsStatus = "loading";
            state.thirdStateAirportsError = null;
        },
        [getThirdStateAirports.fulfilled]: (state, action) => {
            state.thirdStateAirportsStatus = "succeeded";
            state.thirdStateAirports = action.payload || null;
        },
        [getThirdStateAirports.rejected]: (state, action) => {
            state.thirdStateAirportsStatus = "failed";
            state.thirdStateAirports = null;
            state.thirdStateAirportsError = action.error.message;
        },
    }
});

export default timelineFlightListSlice.reducer;

// Actions
export const { reset, setQueryTime, resetUpdateFlightStatus, setFlightSearchTerm, resetGetFlightByIdStatus, resetGetFlightFromDBById } = timelineFlightListSlice.actions;

// Selectors
export const selectStatus = state => state.timelineFlightList.status;
export const selectError = state => state.timelineFlightList.error;
export const selectUpdateFlightStatus = state => state.timelineFlightList.updateFlightStatus;
export const selectUpdateFlightError = state => state.timelineFlightList.updateFlightError;
export const selectQueryTime = state => state.timelineFlightList.queryTime;
export const selectFlightSearchTerm = state => state.timelineFlightList.flightSearchTerm;

// O(1) lookup dict: bookingKey → flight object (cached until flightsByTime changes)
export const selectFlightsByBookingKey = createSelector(
    (state) => state.timelineFlightList.flightsByTime,
    (flightsByTime) => {
        const map = {};
        if (!flightsByTime) return map;
        for (const time in flightsByTime) {
            const flights = flightsByTime[time];
            if (!Array.isArray(flights)) continue;
            for (const flight of flights) {
                if (flight?.bookingKey) map[flight.bookingKey] = flight;
            }
        }
        return map;
    }
);

// Select a single flight by its bookingKey — O(1) via the cached dict
export const selectFlightById = (flightId) =>
    createSelector(
        selectFlightsByBookingKey,
        (flightsMap) => flightsMap[flightId] ?? null
    );

/**
 * Round a Date object to nearest 5-minute interval and return "HH:MM".
 */
function roundToNearest5(date) {
    const minutes = date.getMinutes();
    const rounded = Math.round(minutes / 5) * 5;

    // Rolling over 60 mins
    date.setMinutes(rounded);

    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
}

/**
 * Create a lookup from flight identifiers → flight slot ("HH:MM")
 */
export const selectFlightSlotLookup = createSelector(
    [
        (state) => state.timelineFlightList.flightsByTime,
        (state) => state.timelineControl,
    ],
    (flightsByTime, timelineControl) => {
        const map = {};
        if (!flightsByTime || typeof flightsByTime !== "object") return map;

        Object.entries(flightsByTime).forEach(([slotKey, flights]) => {
            if (!Array.isArray(flights)) return;

            flights.forEach((flight) => {
                if (!flight) return;

                //console.log('processing flight : ', flight);

                const id = flight?.flightId || null;

                const rawTime = flight?.operationTime || flight?.operationInfo?.time || flight?.flightTime || null;

                let slot;

                if (rawTime) {

                    // parse rawTime into Date
                    const d = new Date(rawTime);

                    // check if preboard bookings is on
                    if (timelineControl?.preBoard && flight?.type === "departure") {
                        // subtract 2 hours from rawTime so departure flights map to boarding time
                        d.setHours(d.getHours() - 2);
                    }

                    if (!Number.isNaN(d.getTime())) {
                        // ✔ FIX: Normalize to nearest 5-min interval
                        slot = roundToNearest5(d);
                    }
                }

                // fallback if parsing fails
                if (!slot) slot = slotKey;

                map[id] = slot;

                // check if preboard is turned on
                if (timelineControl?.preBoard && flight?.type === "boarding") {
                    // also map boarding flight id
                    const boardingFlightId = id + '_BOARDING';
                    // adjust slot to be 45 minutes before flight time
                    let boardingTime = new Date(rawTime);
                    boardingTime.setMinutes(boardingTime.getMinutes() - 45);
                    const boardingSlot = roundToNearest5(boardingTime);
                    map[boardingFlightId] = boardingSlot;
                }

            });
        });

        return map;
    }
);

/**
 * OPTIMIZED: Select flights for a specific hour
 * This selector only causes re-renders when the specific hour's data changes
 * 
 * When showAllFlights is false, filters out flights that have no bookings
 * 
 * @param {string} hour - Time slot (e.g., "08:00", "14:30")
 * @returns {Function} Selector function that returns flights for the specified hour
 * 
 * @example
 * const flights0800 = useSelector(selectFlightsByHour("08:00"));
 */

// export const selectFlightsByHour = (hour) =>
//   createSelector(
//     (state) => state.timelineFlightList.flightsByTime[hour],
//     (flights) => flights || []
//   );


export const selectFlightsByHour = (hour) =>
    createSelector(
        [
            (state) => state.timelineFlightList.flightsByTime[hour],
            (state) => state.timelineFlightList.flightSearchTerm,
        ],
        (flights, searchTerm) => {
            if (!flights || flights.length === 0) {
                return [];
            }

            // If no search term, return all flights
            if (!searchTerm || searchTerm.trim() === '') {
                return flights;
            }

            const term = searchTerm.toLowerCase().trim();

            // Filter flights by search term
            return flights.filter(flight => {
                // Search by flight number
                const flightNumber = flight?.flightNumber?.toLowerCase() || '';
                if (flightNumber.includes(term)) return true;

                // Search by origin/destination
                const origin = flight?.origin?.toLowerCase() || '';
                const destination = flight?.destination?.toLowerCase() || '';
                if (origin.includes(term) || destination.includes(term)) return true;

                // Search by booking key
                const bookingKey = flight?.bookingKey?.toLowerCase() || '';
                if (bookingKey.includes(term)) return true;

                // Search by carrier code
                const carrierCode = flight?.carrierCode?.toLowerCase() || '';
                if (carrierCode.includes(term)) return true;

                return false;
            });
        }
    );


// export const selectFlightsByHour = (hour) =>
//   createSelector(
//     [
//       (state) => state.timelineFlightList.flightsByTime[hour],
//       (state) => state.timelineControl.showAllFlights,
//       (state) => state.timelineBookings.bookingsByFlight
//     ],
//     (flights, showAllFlights, bookingsByFlight) => {
//       if (!flights || flights.length === 0) {
//         return [];
//       }

//       // if showAllFlights is true, return all flights
//       if (showAllFlights) {
//         return flights;
//       }

//       // filter flights to only include those with bookings
//       return flights.filter(flight => {
//         // get the flight key to match with bookingsByFlight
//         const flightKey = flight.bookingKey ||
//           (flight?.flightNumber && flight?.origin && flight?.destination && flight?.flightDate
//             ? `${flight?.flightNumber}_${flight?.origin}_${flight?.destination}_${flight?.flightDate}`
//             : null);

//         if (!flightKey) {
//           // if we can't construct a key, exclude the flight when showAllFlights is false
//           return false;
//         }

//         // check if there are bookings for this flight
//         const bookings = bookingsByFlight?.[flightKey];
//         return bookings && Array.isArray(bookings) && bookings.length > 0;
//       });
//     }
//   );


/**
 * Select all flights grouped by time
 */
export const selectFlightsByTime = state => {
    return state.timelineFlightList.flightsByTime;
};

/**
 * Select all hour slots that have flights
 */
export const selectAvailableHours = state => {
    return Object.keys(state.timelineFlightList.flightsByTime).sort();
};

/**
 * Select all flights as a flat array
 */
export const selectFlightsArray = state => {
    return Object.values(state.timelineFlightList.flightsByTime).flat();
};

/**
 * Select total flight count
 */
export const selectTotalFlightCount = state => {
    return Object.values(state.timelineFlightList.flightsByTime).reduce(
        (total, flights) => total + (flights?.length || 0),
        0
    );
};

export const selectFlightFromDBById = (state) => state.timelineFlightList.flightFromDBById;
export const selectGetFlightByIdStatus = state => state.timelineFlightList.getFlightByIdStatus;

export const selectThirdStateAirports = state => state.timelineFlightList.thirdStateAirports;
export const selectThirdStateAirportsStatus = state => state.timelineFlightList.thirdStateAirportsStatus;